import React from 'react';

import { PROTOTYPE_SCREENS as FAMILY_SCREENS } from '../../../design-system/family-screens.generated';
import { PROTOTYPE_SCREENS as TEACHER_SCREENS } from '../../../design-system/teacher-screens.generated';

const BUNDLES = { teacher: TEACHER_SCREENS, family: FAMILY_SCREENS };

interface Props {
  /** Which prototype the markup comes from. */
  environment: keyof typeof BUNDLES;
  /** Key into the generated prototype markup, e.g. 'students'. */
  screen: string;
  title: string;
}

/**
 * A screen the redesign specifies and the product has not built.
 *
 * It renders the prototype's own markup so the navigation is complete and no
 * link in a walkthrough is dead. What it must never do is look finished: the
 * banner says the data is illustrative, because a screen that renders
 * plausible numbers with nothing behind them is the most expensive kind of
 * misunderstanding a demo can create.
 *
 * The markup is committed generated output from docs/redesign, with handlers
 * and outbound links stripped by the generator — it is our own static string,
 * not anything a user supplied.
 *
 * The wrapper's id matters. The prototype scopes each screen's styles to its
 * own element — `#s_dashboard .mc`, `#s_students .srow` — so a fragment
 * rendered without that id inherits the tokens and none of the layout, and
 * arrives looking like unstyled markup rather than like the design.
 */
const PrototypeScreen: React.FC<Props> = ({ environment, screen, title }) => {
  const markup = BUNDLES[environment][screen];

  return (
    <>
      <div
        role="note"
        style={{
          margin: '16px 20px 0',
          padding: '10px 14px',
          borderRadius: 12,
          background: 'var(--amber-soft, #FFF4DE)',
          border: '1px solid rgba(255,176,32,.35)',
          color: '#7A5200',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        Diseño aprobado, sin datos reales todavía — {title} aún no está conectado al backend.
      </div>
      {markup ? (
        <div id={`s_${screen}`} dangerouslySetInnerHTML={{ __html: markup }} />
      ) : (
        <div style={{ padding: 20 }}>Esta pantalla aún no existe en el prototipo.</div>
      )}
    </>
  );
};

export default PrototypeScreen;
